import ConnectSDK from '../ConnectSDK';
import { OauthCallbackResponse } from '../entities/credential.interface';
import { IIntegrationMetadata } from '../entities/integration.interface';
import { PersonaMeta } from '../entities/persona.interface';
import { IConnectUserContext } from '../helpers/ConnectUserContext';
import {
  AuthenticatedConnectUser,
  DynamicMappingField,
  DynamicMappingOptions,
  IntegrationWorkflowState,
} from '../types/connect';

import { Props as ConnectModalProps } from './connectModal';

export type ConnectUser = { authenticated: false } | AuthenticatedConnectUser;

export type FunctionPropertyNames<T> = {
  [K in keyof T]: T[K] extends Function ? K : never;
}[keyof T];
type NonFunctionPropertyNames<T> = {
  [K in keyof T]: T[K] extends Function ? never : K;
}[keyof T];
export type NonFunctionProperties<T> = Pick<T, NonFunctionPropertyNames<T>>;
export type UnwrapPromise<T> = T extends PromiseLike<infer U> ? U : T;

export type NonNullUndefinedField<T> = {
  [P in keyof T]-?: NonNullUndefinedField<NonNullable<T[P]>>;
};

export type UIUpdateMessage = {
  messageType: 'UI_UPDATE';
  nextContext: NonFunctionProperties<IConnectUserContext>;
  nextModalState: NonFunctionProperties<ConnectModalProps>;
};

export type SDKFunctionInvocationMessage<T extends FunctionPropertyNames<ConnectSDK> = never> = {
  messageType: 'SDK_FUNCTION_INVOCATION';
  type: T;
  parameters: Parameters<ConnectSDK[T]>;
  id: string;
};

export type SDKFunctionResponseMessage<T extends FunctionPropertyNames<ConnectSDK> = never> = {
  messageType: 'SDK_FUNCTION_RESPONSE';
  type: T;
  result: UnwrapPromise<ReturnType<ConnectSDK[T]>>;
  id: string;
};

export type SDKFunctionErrorMessage = {
  messageType: 'SDK_FUNCTION_ERROR';
  error: true;
  message: string;
  id: string;
};

export type SDKReceivedMessage = SDKFunctionInvocationMessage;

export interface IConnectSDK {
  authenticate(projectId: string, token: string, options?: AuthenticateOptions): Promise<void>;
  connect(action: string): void;
  getUser(): ConnectUser;
  /**
   * **Do not call this function directly.**
   *
   * Invoked by the OAuth popup after finishing the credential exchange.
   * @private
   */
  _oauthCallback(credential: OauthCallbackResponse): Promise<void>;

  /**
   * **Do not call this function directly.**
   *
   * Invoked by the OAuth popup on an error encountered during the credential exchange.
   * @private
   */
  _oauthErrorCallback(errorMessage: string): Promise<void>;

  /**
   * installs an integration without the need to click on enable button from portal
   * Install Options:
   * `{showPortalAfterInstall: boolean}` decides wether to show portal with configuration view after the installation is completed
   */
  installIntegration(name: string, options: InstallOptions): Promise<void>;

  /**
   * uninstalls an integration for connected user.
   */
  uninstallIntegration(name: string): Promise<void>;

  /**
   * disables a workflow for the connected user and unsubscribes all the workflows in hermes
   */
  disableWorkflow(workflowId: string): Promise<void>;

  setUserMetadata(meta: PersonaMeta): Promise<AuthenticatedConnectUser>;

  /**
   * sets the base url for different services used in connect sdk
   */
  configureGlobal(options: { host: string }): void;

  /**
   * returns metadata for given integration
   * @param integrationName integration name
   */
  getIntegrationMetadata(integrationName: string): IIntegrationMetadata;
  getIntegrationMetadata(): IIntegrationMetadata[];

  /**
   * triggers all workflows for event
   * @param name eventName
   * @param payload event payload object
   */
  event(name: string, payload: Record<string, unknown>): Promise<void>;

  /**
   * makes an connect proxy action request
   * @param action actionName
   * @param path request path
   * @param init request initialization options {method: httpMethod, body:RequestBody, header}
   */
  request<TResponse>(
    action: string,
    path: string,
    init: {
      method: RequestInit['method'];
      body: RequestInit['body'] | object;
      headers: RequestInit['headers'];
    },
  ): Promise<TResponse | undefined>;

  /**
   * listen on connect sdk events
   * @param eventName SDKEVENTS
   * @param listener
   */
  subscribe(eventName: SDK_EVENT, listener: ListenerFunction): () => boolean;

  /**
   * remove listeners on connect sdk events
   * @param eventName SDKEVENTS
   * @param listener
   */
  unsubscribe(eventName: SDK_EVENT, listener: ListenerFunction): boolean;

  /**
   * trigger connect endpoint trigger workflow
   * @param workflowId
   * @param triggerWorkflowRequest
   */
  workflow(
    workflowId: string,
    triggerWorkflowRequest: TriggerWorkflowRequest,
  ): Promise<object | undefined>;
}

/**
 * sdk install options
 */
export type InstallOptions = {
  /**
   * api only install or ui install
   */
  isApiInstallation?: boolean;
  /**
   * if true show portal when install with api install option
   */
  showPortalAfterInstall?: boolean;
  /**
   * not show post oauth promt if this is true
   */
  bypassPostOAuthPrompt?: boolean;
};

export type AuthenticateOptions = {
  metadata?: PersonaMeta;
};

/**
 * Connect Modal Tabs
 */
export enum ModalView {
  OVERVIEW = 'overview',
  CONFIGURATION = 'configuration',
}

export type IntegrationInstallEvent = {
  integrationId: string;
  integrationType: string;
};

export type IntegrationUninstallEvent = IntegrationInstallEvent;

export type WorkflowStateChangeEvent = {
  integrationId: string;
  workflowId: string;
  workflowStateChange: Partial<IntegrationWorkflowState>;
};

export type PortalOpenEvent = {
  integrationId: string;
  integrationType: string;
};

export type PortalCloseEvent = PortalOpenEvent;

export type CallbackMap = {
  onSuccess?: (event: IntegrationInstallEvent, user: AuthenticatedConnectUser) => void;
  onInstall?: (event: IntegrationInstallEvent, user: AuthenticatedConnectUser) => void;
  onError?: (err: Error) => void;
  onUninstall?: (event: IntegrationUninstallEvent, user: AuthenticatedConnectUser) => void;
  onWorkflowChange?: (event: WorkflowStateChangeEvent, user: AuthenticatedConnectUser) => void;
  onOpen?: (event: PortalOpenEvent, user: AuthenticatedConnectUser) => void;
  onClose?: (event: PortalCloseEvent, user: AuthenticatedConnectUser) => void;
};

export type UserProvidedIntegrationConfig = {
  /**
   * used to override redirect url passed in getAuthUrl instead of our passport url
   */
  overrideRedirectUrl?: string;

  /**
   * This will allow users to provide dynamic fields options for object mapping inputs
   */
  mapObjectFields?: {
    [objectName: string]: DynamicMappingField[] | DynamicMappingOptions;
  };
};

export type ConnectParams = CallbackMap & UserProvidedIntegrationConfig & InstallOptions;

export type EventInfo = {
  type: SDK_EVENT;
  integrationId: string;
  integrationType: string;
  workflowId?: string;
  workflowStateChange?: Partial<IntegrationWorkflowState>;
};

export enum SDK_EVENT {
  ON_INTEGRATION_INSTALL = 'onIntegrationInstall',
  ON_INTEGRATION_UNINSTALL = 'onIntegrationUninstall',
  ON_WORKFLOW_CHANGE = 'onWorkflowChange',
  ON_PORTAL_OPEN = 'onPortalOpen',
  ON_PORTAL_CLOSE = 'onPortalClose',
}

export type SDKEventListenerMap = {
  [SDK_EVENT.ON_INTEGRATION_INSTALL]: Array<
    (event: IntegrationInstallEvent, user: AuthenticatedConnectUser) => void
  >;
  [SDK_EVENT.ON_INTEGRATION_UNINSTALL]: Array<
    (event: IntegrationUninstallEvent, user: AuthenticatedConnectUser) => void
  >;
  [SDK_EVENT.ON_PORTAL_CLOSE]: Array<
    (event: PortalCloseEvent, user: AuthenticatedConnectUser) => void
  >;
  [SDK_EVENT.ON_PORTAL_OPEN]: Array<
    (event: PortalOpenEvent, user: AuthenticatedConnectUser) => void
  >;
  [SDK_EVENT.ON_WORKFLOW_CHANGE]: Array<
    (event: WorkflowStateChangeEvent, user: AuthenticatedConnectUser) => void
  >;
};

export type ListenerFunction = SDKEventListenerMap[keyof SDKEventListenerMap][number];

export enum DocumentLoadingState {
  LOADING = 'loading',
  INTERACTIVE = 'interactive',
  COMPLETE = 'complete',
}

export type TriggerWorkflowRequest = {
  body?: RequestInit['body'] | object;
  headers?: RequestInit['headers'];
  query?: Record<string, string>;
};
